/* Tickling Toe · Sticker Book theme · core motion + commerce
   Depends on gsap, ScrollTrigger, SplitText, Draggable, InertiaPlugin, Lenis (all deferred, loaded before this file). */
(() => {
  'use strict';
  const d = document, html = d.documentElement, w = window;
  const TT = w.TT || (w.TT = {});
  const $ = (s, c = d) => c.querySelector(s);
  const $$ = (s, c = d) => Array.from(c.querySelectorAll(s));
  const still = html.classList.contains('is-still');
  const mqDesk = w.matchMedia('(min-width: 750px)');
  const fine = html.classList.contains('has-hover');
  const hasGsap = !!w.gsap;
  const store = {
    get(k, f) { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : f; } catch (e) { return f; } },
    set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} }
  };

  /* GSAP + CSS transitions on the same transform fight each other: switch CSS transitions off
     while GSAP animates an element, then hand the element back to CSS. */
  function pop(targets, vars) {
    const els = (w.gsap ? gsap.utils.toArray(targets) : []);
    if (!els.length) return null;
    els.forEach(e => { e.style.transition = 'none'; });
    const done = vars.onComplete;
    return gsap.from(els, { ...vars, onComplete() { gsap.set(els, { clearProps: 'transform,transition,opacity,scale,rotate,translate' }); done && done(); } });
  }
  TT.pop = pop;

  /* ------------------------------------------------------------------ boot */
  html.classList.add('tt-wait');
  const release = () => { html.classList.remove('tt-wait'); html.classList.add('tt-ready'); };
  setTimeout(release, 2500); // failsafe: never leave content hidden

  if (hasGsap) {
    gsap.registerPlugin(ScrollTrigger);
    w.SplitText && gsap.registerPlugin(SplitText);
    w.Draggable && gsap.registerPlugin(Draggable);
    w.InertiaPlugin && gsap.registerPlugin(InertiaPlugin);
    ScrollTrigger.config({ ignoreMobileResize: true });
    if (still) gsap.globalTimeline.timeScale(100);
  }

  /* ------------------------------------------------------------------ smooth scroll */
  let lenis = null;
  function initLenis() {
    if (!w.Lenis || still || !TT.motion?.smooth || !fine) return;
    lenis = new Lenis({ lerp: 0.12, smoothWheel: true, syncTouch: false });
    TT.lenis = lenis;
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(t => lenis.raf(t * 1000));
    gsap.ticker.lagSmoothing(0);
  }
  const lockScroll = on => {
    if (lenis) on ? lenis.stop() : lenis.start();
    html.style.overflow = on ? 'hidden' : '';
  };

  /* ------------------------------------------------------------------ header + dock: hide on scroll down */
  function initChrome() {
    let last = 0, ticking = false;
    const onScroll = () => {
      const y = w.scrollY;
      html.classList.toggle('is-scrolled', y > 40);
      const down = y > last && y > 260;
      html.classList.toggle('is-hidden-hd', down && !html.classList.contains('menu-open'));
      html.classList.toggle('is-dock-away', down);
      last = y; ticking = false;
    };
    w.addEventListener('scroll', () => { if (!ticking) { requestAnimationFrame(onScroll); ticking = true; } }, { passive: true });
    onScroll();
  }

  /* ------------------------------------------------------------------ marquee: endless, reacts to scroll speed */
  function initMarquees() {
    $$('[data-marquee]').forEach(mq => {
      const track = $('.mq__track', mq), first = $('.mq__group', mq);
      if (!track || !first || still || !hasGsap) return;
      const pxPerSec = +mq.dataset.speed || 60;
      let width = first.offsetWidth, x = 0, dir = -1, boost = 0;
      const resize = () => { width = first.offsetWidth; };
      w.addEventListener('resize', resize);
      ScrollTrigger.create({ onUpdate: s => { boost = Math.min(Math.abs(s.getVelocity()) / 80, 18); dir = s.direction === 1 ? -1 : 1; } });
      gsap.ticker.add((t, dt) => {
        boost *= 0.92;
        x += dir * (pxPerSec + boost * pxPerSec * 0.35) * (dt / 1000);
        if (x <= -width) x += width; if (x > 0) x -= width;
        track.style.transform = `translate3d(${x}px,0,0)`;
      });
    });
  }

  /* ------------------------------------------------------------------ text: elastic letter tumble + extruded rise */
  const splits = new WeakMap();
  function split(el, type = 'chars,words') {
    if (!w.SplitText) return null;
    if (splits.has(el)) return splits.get(el);
    const s = new SplitText(el, { type, charsClass: 'char', wordsClass: 'word' });
    splits.set(el, s); return s;
  }
  function tumble(el, delay = 0) {
    const s = split(el); if (!s) return gsap.timeline();
    gsap.set(el, { perspective: 1200 });
    return gsap.fromTo(s.chars,
      { yPercent: 160, rotateX: 80, rotateY: 20, rotateZ: 18, opacity: 0 },
      { yPercent: 0, rotateX: 0, rotateY: 0, rotateZ: 0, opacity: 1, duration: 1.15, ease: 'elastic.out(0.6,0.45)', stagger: 0.025, delay });
  }
  function rise(el, delay = 0) {
    const s = split(el); if (!s) return gsap.timeline();
    // a tangerine copy sits behind each letter, settling a beat later: the letters look extruded for a moment
    const backs = s.chars.map(c => { const b = d.createElement('span'); b.className = 'char-back'; b.textContent = c.textContent; b.setAttribute('aria-hidden', 'true'); c.style.position = 'relative'; c.appendChild(b); return b; });
    gsap.set(backs, { position: 'absolute', left: 0, top: 0, zIndex: -1, color: 'var(--tt-bubble)', webkitTextStroke: '0px', textShadow: 'none', yPercent: 24 });
    const tl = gsap.timeline({ delay });
    tl.from(s.chars, { yPercent: 110, opacity: 0, rotate: () => gsap.utils.random(-14, 14), duration: 0.9, ease: 'back.out(2.2)', stagger: 0.045 }, 0)
      .to(backs, { yPercent: 0, duration: 1.1, ease: 'power2.out', stagger: 0.045 }, 0.05)
      .set(backs, { opacity: 0 });
    return tl;
  }
  function initTitles() {
    if (still || !hasGsap) return;
    $$('[data-tumble]').forEach(el => {
      if (el.closest('[data-hero]')) return;
      gsap.set(el, { opacity: 0 });
      ScrollTrigger.create({ trigger: el, start: 'top 88%', once: true, onEnter: () => { gsap.set(el, { opacity: 1 }); tumble(el); } });
    });
  }

  /* ------------------------------------------------------------------ cards pop in */
  function initReveal() {
    const items = $$('[data-reveal]');
    if (still || !hasGsap) { items.forEach(i => i.classList.add('is-in')); return; }
    items.forEach(el => el.style.setProperty('--rr', gsap.utils.random(-5, 5, 1) + 'deg'));
    ScrollTrigger.batch(items, {
      start: 'top 92%', once: true,
      onEnter: batch => batch.forEach((el, i) => setTimeout(() => el.classList.add('is-in'), i * 90))
    });
  }
  TT.reveal = (root) => {
    const items = $$('[data-reveal]:not(.is-in)', root);
    items.forEach((el, i) => setTimeout(() => el.classList.add('is-in'), 60 + i * 60));
    initTickle(root);
    initWish(root);
  };

  /* ------------------------------------------------------------------ spinning stickers (only while visible) */
  function initSpin() {
    if (still || !hasGsap) return;
    $$('[data-spin]').forEach(el => {
      let rot = Math.random() * 30 - 15; const dir = Math.random() < 0.5 ? -1 : 1, speed = 0.12 + Math.random() * 0.12;
      const set = gsap.quickSetter(el, 'rotation', 'deg');
      const tick = (t, dt) => { rot += speed * dir * (dt / 16.667); set(rot); };
      ScrollTrigger.create({ trigger: el, start: 'top bottom', end: 'bottom top', onToggle: s => s.isActive ? gsap.ticker.add(tick) : gsap.ticker.remove(tick) });
    });
  }

  /* ------------------------------------------------------------------ Toey: toes wave one by one ("this little piggy") */
  function toeWave(svg, opts = {}) {
    if (!svg || !hasGsap || still) return gsap.timeline();
    const toes = $$('.toey__toe', svg);
    return gsap.timeline({ repeat: opts.repeat || 0 })
      .set(toes, { transformOrigin: '50% 100%' })
      .to(toes, { keyframes: [{ rotation: -18, y: -3, duration: 0.09 }, { rotation: 14, y: 0, duration: 0.11 }, { rotation: 0, duration: 0.16, ease: 'back.out(3)' }], stagger: 0.06, ease: 'sine.inOut' });
  }
  function giggleFace(svg, times = 3) {
    if (!svg || !hasGsap || still || !$('.toey__mouth', svg)) return gsap.timeline();
    return gsap.timeline()
      .set($$('.toey__eye', svg), { transformOrigin: '50% 50%' }, 0)
      .set($('.toey__mouth', svg), { transformOrigin: '50% 0%' }, 0)
      .to($$('.toey__eye', svg), { scaleY: 0.12, duration: 0.07, yoyo: true, repeat: times * 2 - 1, ease: 'none' }, 0)
      .to($('.toey__mouth', svg), { scale: 1.3, duration: 0.12, yoyo: true, repeat: times * 2 - 1, ease: 'sine.inOut' }, 0);
  }
  TT.toeWave = toeWave;
  // the logo can't wave toes, so it squirms like it's being tickled
  function logoGiggle(img, times = 3) {
    if (!img || !hasGsap || still) return gsap.timeline();
    return gsap.timeline()
      .set(img, { transformOrigin: '50% 90%' })
      .to(img, { keyframes: [{ rotation: -6, x: -4, scaleY: 0.94 }, { rotation: 5, x: 4, scaleY: 1.04 }], duration: 0.16, repeat: times - 1, ease: 'sine.inOut' })
      .to(img, { rotation: 0, x: 0, scaleY: 1, duration: 0.4, ease: 'elastic.out(1,0.4)' });
  }

  /* ------------------------------------------------------------------ loader (first visit, home): the logo gets tickled */
  function runLoader() {
    const el = $('[data-loader]');
    if (!el || !hasGsap) { el && el.remove(); return Promise.resolve(); }
    try { sessionStorage.setItem('tt-loaded', '1'); } catch (e) {}
    const logo = $('.loader__logo', el), feather = $('.loader__feather', el), shadow = $('.loader__shadow', el);
    const giggles = $$('.loader__g', el), sparks = $$('.loader__spark', el), words = $$('.loader__word span', el);
    return new Promise(res => {
      const tl = gsap.timeline({ onComplete: () => { el.remove(); res(); } });
      gsap.set(el, { clipPath: 'circle(150% at 50% 45%)' });
      gsap.set(giggles, { scale: 0, opacity: 0 });
      tl
        // the logo drops in and lands with a squish
        .from(logo, { y: -300, rotation: -18, duration: 0.42, ease: 'power2.in' })
        .from(shadow, { scaleX: 0.2, opacity: 0, duration: 0.42, ease: 'power2.in' }, 0)
        .to(logo, { scaleY: 0.8, scaleX: 1.12, transformOrigin: '50% 100%', duration: 0.08, ease: 'power1.out' })
        .to(logo, { scaleY: 1, scaleX: 1, duration: 0.5, ease: 'elastic.out(1,0.35)' })
        .from(words, { y: 26, opacity: 0, rotation: () => gsap.utils.random(-12, 12), stagger: 0.06, duration: 0.45, ease: 'back.out(3)' }, 0.3)
        .from(sparks, { scale: 0, rotation: -120, duration: 0.5, ease: 'back.out(3)', stagger: 0.08 }, 0.42)
        // the feather swoops in…
        .from(feather, { x: 220, y: -60, rotation: 60, opacity: 0, duration: 0.38, ease: 'power3.out' }, 0.5)
        // …and tickles the logo
        .to(feather, { keyframes: [{ x: -12, y: -6, rotation: -22 }, { x: 8, y: 4, rotation: 10 }, { x: -12, y: -6, rotation: -22 }, { x: 8, y: 4, rotation: 10 }, { x: -6, y: 0, rotation: -10 }], duration: 0.6, ease: 'sine.inOut' }, 0.88)
        .add(logoGiggle(logo, 3), 0.9)
        .to(giggles, { scale: 1, opacity: 1, duration: 0.3, ease: 'back.out(3)', stagger: 0.16 }, 0.95)
        // giggle jump, then the yellow page closes into the logo
        .to(logo, { y: -28, duration: 0.18, ease: 'power2.out' }, 1.5)
        .to(logo, { y: 0, duration: 0.3, ease: 'bounce.out' }, 1.68)
        .to(feather, { x: 260, rotation: 70, opacity: 0, duration: 0.3, ease: 'power2.in' }, 1.5)
        .to(el, { clipPath: 'circle(0% at 50% 45%)', duration: 0.55, ease: 'power3.inOut' }, 1.82);
    });
  }

  /* ------------------------------------------------------------------ hero */
  function initHero() {
    const hero = $('[data-hero]');
    if (!hero) return;
    if (still || !hasGsap) { release(); return; }
    const stage = $('[data-hero-stage]', hero);
    const l1 = $('.hero__l1', hero), l2 = $('.hero__l2', hero);
    const kids = $$('[data-kid]', hero), stickers = $$('[data-sticker]', hero);
    const pops = $$('[data-pop]', hero);

    const tl = gsap.timeline({ paused: true });
    gsap.set([...pops, ...kids, ...stickers], { opacity: 0 });
    release();
    tl.add(tumble(l1), 0)
      .add(rise(l2), 0.35)
      .to(pops, { opacity: 1, duration: 0.01 }, 0.1)
      .from(pops, { y: 30, rotate: () => gsap.utils.random(-6, 6), duration: 0.9, ease: 'elastic.out(0.7,0.5)', stagger: 0.12 }, 0.1)
      .from('.hero__sun', { scale: 0, rotate: -90, duration: 1.1, ease: 'elastic.out(0.8,0.5)' }, 0.1)
      .to(kids, { opacity: 1, duration: 0.01 }, 0.35)
      .from(kids, { yPercent: 60, scale: 0.5, rotate: (i) => [8, -14, 12][i] || 0, duration: 1.2, ease: 'elastic.out(0.7,0.45)', stagger: 0.12 }, 0.35);

    // stickers burst in from outside the screen along the ray from the centre
    const cx = innerWidth / 2, cy = innerHeight / 2;
    stickers.forEach(el => {
      const r = el.getBoundingClientRect();
      const a = Math.atan2(r.top + r.height / 2 - cy, r.left + r.width / 2 - cx);
      gsap.set(el, { x: Math.cos(a) * innerWidth * 0.8, y: Math.sin(a) * innerHeight * 0.8, rotate: gsap.utils.random(-180, 180) });
    });
    tl.to(stickers, { opacity: 1, duration: 0.01 }, 0.6)
      .to(stickers, { x: 0, y: 0, rotate: 0, duration: 1.8, ease: 'elastic.out(0.6,0.7)', stagger: { from: 'random', each: 0.08 } }, 0.6)
      .from('.hero__tickle', { scale: 0, rotate: -30, duration: 0.6, ease: 'back.out(3)' }, 1.4)
      .call(() => makeDraggable(stickers, stage), null, 2.2);

    TT.heroIntro = tl;

    // pointer parallax (desktop) — kids lean towards the cursor
    if (fine) {
      const qx = kids.map(k => gsap.quickTo(k, 'x', { duration: 0.8, ease: 'power3' }));
      const qr = kids.map(k => gsap.quickTo(k, 'rotation', { duration: 0.8, ease: 'power3' }));
      hero.addEventListener('pointermove', e => {
        const nx = e.clientX / innerWidth - 0.5;
        qx.forEach((q, i) => q(nx * (18 + i * 10)));
        qr.forEach((q, i) => q(nx * (4 + i * 2)));
      });
    }
    // scroll: the stage drifts up and the sun rolls
    gsap.to('.hero__sun', { rotate: 40, yPercent: -8, ease: 'none', scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true } });
    gsap.to(kids, { yPercent: (i) => -10 - i * 6, ease: 'none', scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true } });

    // Toey waves its toes every few seconds, and on hover / tap
    const heroToey = $('.hero__toey-art', hero);
    if (heroToey) {
      gsap.delayedCall(3, function wave() { toeWave(heroToey); gsap.delayedCall(gsap.utils.random(3, 5), wave); });
      heroToey.closest('[data-toey]')?.addEventListener('pointerenter', () => { toeWave(heroToey); giggleFace(heroToey, 2); });
    }
        // tickle the kids: a wiggle on hover / tap
    kids.forEach(k => {
      const wig = () => gsap.fromTo(k, { rotation: 0 }, { rotation: 6, duration: 0.09, yoyo: true, repeat: 5, ease: 'sine.inOut', overwrite: 'auto', onComplete: () => gsap.to(k, { rotation: 0, duration: 0.3 }) });
      k.addEventListener('mouseenter', wig);
      k.addEventListener('touchstart', wig, { passive: true });
    });
  }

  /* draggable stickers with a throw (sticker book!) */
  function makeDraggable(els, bounds) {
    if (!w.Draggable) return;
    els.forEach(el => {
      Draggable.create(el, {
        type: 'x,y', bounds: bounds || d.body, inertia: !!w.InertiaPlugin, edgeResistance: 0.65,
        onPress() { gsap.to(this.target, { scale: 1.15, rotation: gsap.utils.random(-15, 15), duration: 0.25, ease: 'back.out(3)' }); this.target.style.zIndex = 20; },
        onRelease() { gsap.to(this.target, { scale: 1, duration: 0.5, ease: 'elastic.out(0.8,0.4)' }); },
        onClick() { gsap.fromTo(this.target, { rotation: -12 }, { rotation: 12, yoyo: true, repeat: 3, duration: 0.08, onComplete: () => gsap.to(this.target, { rotation: 0, duration: 0.3 }) }); }
      });
    });
  }

  /* ------------------------------------------------------------------ vibes: swipe deck on phone, dealt cards on desktop */
  function initVibes() {
    $$('[data-vibes]').forEach(sec => {
      const deck = $('[data-deck]', sec);
      const cards = $$('[data-card]', deck);
      if (!cards.length || still || !hasGsap) return;
      const mm = gsap.matchMedia();
      mm.add('(min-width: 750px)', () => {
        cards.forEach(c => { c.style.opacity = 0; });
        ScrollTrigger.create({ trigger: deck, start: 'top 80%', once: true, onEnter: () => { cards.forEach(c => { c.style.opacity = ''; }); pop(cards, { y: 160, rotation: (i) => (i % 2 ? 14 : -14), opacity: 0, duration: 1.2, ease: 'elastic.out(0.7,0.55)', stagger: 0.12 }); } });
      });
      mm.add('(max-width: 749px)', () => {
        let order = cards.slice();
        const layout = (animate = true) => order.forEach((c, i) => {
          const props = { x: 0, y: i * 14, rotation: i === 0 ? 0 : (i % 2 ? 4 : -4) * Math.min(i, 2), scale: 1 - i * 0.05, zIndex: 10 - i, opacity: i > 3 ? 0 : 1 };
          animate ? gsap.to(c, { ...props, duration: 0.6, ease: 'elastic.out(0.8,0.6)' }) : gsap.set(c, props);
        });
        layout(false);
        gsap.from(deck, { y: 120, opacity: 0, rotation: -6, duration: 1.1, ease: 'elastic.out(0.7,0.5)', scrollTrigger: { trigger: deck, start: 'top 85%', once: true } });
        let moved = false;
        const drags = cards.map(card => Draggable.create(card, {
          type: 'x', allowNativeTouchScrolling: true, zIndexBoost: false,
          onPress() { moved = false; if (card !== order[0]) this.endDrag(); },
          onDrag() { moved = true; gsap.set(card, { rotation: this.x / 14 }); },
          onRelease() {
            if (card !== order[0]) return;
            if (Math.abs(this.x) > 80) {
              const dir = this.x > 0 ? 1 : -1;
              gsap.to(card, { x: dir * innerWidth, rotation: dir * 30, duration: 0.35, ease: 'power2.in', onComplete: () => { order.push(order.shift()); layout(); } });
            } else { layout(); }
          }
        })[0]);
        deck.addEventListener('click', e => { if (moved) { e.preventDefault(); moved = false; } }, true);
        return () => { drags.forEach(dr => dr.kill()); gsap.set(cards, { clearProps: 'all' }); };
      });
    });
  }

  /* ------------------------------------------------------------------ twirl band: scroll-scrubbed twirl + flowers fill */
  function initTwirlBand() {
    $$('[data-twirl-band]').forEach(sec => {
      const flowers = $$('.twb__f', sec), dresses = $$('[data-twirl-dress] > img, [data-twirl-dress] > svg.die', sec);
      const note = $('[data-twirl-note]', sec);
      if (still || !hasGsap) { flowers.forEach(f => f.classList.add('is-on')); return; }
      gsap.set(note, { opacity: 0, scale: 0.6 });
      const pin = mqDesk.matches && innerHeight > 620;
      const tl = gsap.timeline({ scrollTrigger: { trigger: sec, start: pin ? 'top top' : 'top 75%', end: pin ? '+=120%' : 'bottom 40%', scrub: 0.6, pin: pin ? $('.twb__pin', sec) : false,
        onUpdate: s => { const n = Math.round(s.progress * 5.4); flowers.forEach((f, i) => f.classList.toggle('is-on', i < n)); } } });
      dresses.forEach((dr, i) => {
        const s = i ? -1 : 1;
        tl.to(dr, { keyframes: [ { rotation: 10 * s, scaleX: 0.86, duration: 0.25 }, { rotation: -8 * s, scaleX: 1.06, duration: 0.25 }, { rotation: 6 * s, scaleX: 0.92, duration: 0.25 }, { rotation: 0, scaleX: 1, duration: 0.25 } ], ease: 'sine.inOut' }, 0);
      });
      tl.to('.twb__swish', { rotation: 360, transformOrigin: '50% 100%', ease: 'none', duration: 1 }, 0)
        .to(note, { opacity: 1, scale: 1, rotation: -3, ease: 'back.out(3)', duration: 0.15 }, 0.85);
      gsap.to($$('.twb__spark', sec), { scale: 1.4, rotation: 90, yoyo: true, repeat: -1, duration: 1.2, ease: 'sine.inOut', stagger: 0.4 });
    });
  }

  /* ------------------------------------------------------------------ growth tape: Toey climbs to the age */
  function initTape() {
    $$('[data-tape]').forEach(tape => {
      const toey = $('[data-tape-toey]', tape), say = $('[data-tape-say]', tape);
      const chips = $$('.tape__chip', tape);
      const saved = store.get('tt-age', null);
      const lines = ['this tall?', 'ooh, big kid!', 'perfect fit', 'growing fast!', 'hehe!'];
      const moveTo = (chip) => {
        const r = chip.getBoundingClientRect(), tr = tape.getBoundingClientRect();
        toey.style.setProperty('--tx', (r.left - tr.left + r.width / 2 - 29) + 'px');
        say.textContent = lines[chips.indexOf(chip) % lines.length];
        if (hasGsap && !still) gsap.fromTo(toey, { y: -16 }, { y: 0, duration: 0.6, ease: 'bounce.out' });
      };
      chips.forEach(c => {
        if (saved && c.dataset.age === saved) { c.classList.add('is-on'); requestAnimationFrame(() => moveTo(c)); }
        c.addEventListener('mouseenter', () => moveTo(c));
        c.addEventListener('focus', () => moveTo(c));
      });
      if (hasGsap && !still) ScrollTrigger.create({ trigger: tape, start: 'top 85%', once: true, onEnter: () => pop(chips, { y: -60, rotation: () => gsap.utils.random(-30, 30), opacity: 0, duration: 1, ease: 'bounce.out', stagger: 0.06 }) });
    });
  }
  function initAgeMemory() {
    d.addEventListener('click', e => { const a = e.target.closest('[data-age]'); if (a) store.set('tt-age', a.dataset.age); });
    const saved = store.get('tt-age', null);
    if (saved) $$('.ages [data-age]').forEach(c => c.classList.toggle('is-on', c.dataset.age === saved));
  }

  /* ------------------------------------------------------------------ runway: polaroids slide with scroll (desktop) */
  function initRunway() {
    $$('[data-runway]').forEach(sec => {
      const track = $('[data-runway-track]', sec);
      if (!track || still || !hasGsap) return;
      const mm = gsap.matchMedia();
      mm.add('(min-width: 1100px)', () => {
        const dist = () => Math.max(0, track.scrollWidth - track.clientWidth + 80);
        gsap.fromTo(track, { x: 60 }, { x: () => -dist(), ease: 'none', scrollTrigger: { trigger: sec, start: 'top 70%', end: 'bottom top', scrub: 0.8, invalidateOnRefresh: true } });
      });
      ScrollTrigger.create({ trigger: sec, start: 'top 80%', once: true, onEnter: () => pop($$('[data-pol]', sec), { y: 80, rotation: () => gsap.utils.random(-20, 20), opacity: 0, duration: 1, ease: 'elastic.out(0.7,0.5)', stagger: 0.08 }) });
    });
  }

  /* ------------------------------------------------------------------ footer: sticker pile you can fling + jiggly wordmark */
  function initFooter() {
    const ft = $('[data-footer]'); if (!ft || !hasGsap) return;
    const sts = $$('.ft__st', ft);
    if (!still) {
      gsap.set(sts, { y: -260, rotation: () => gsap.utils.random(-120, 120), opacity: 0 });
      ScrollTrigger.create({ trigger: ft, start: 'top 90%', once: true, onEnter: () => {
        gsap.to(sts, { y: () => gsap.utils.random(-6, 40), rotation: () => gsap.utils.random(-25, 25), opacity: 1, duration: 1.2, ease: 'bounce.out', stagger: { each: 0.07, from: 'random' } });
      } });
      ScrollTrigger.create({ trigger: '.ft__word', start: 'top 95%', once: true, onEnter: () => pop($$('.ft__word span', ft), { yPercent: 110, rotation: () => gsap.utils.random(-25, 25), duration: 1, ease: 'elastic.out(0.8,0.5)', stagger: 0.04 }) });
    }
    makeDraggable(sts, ft);
  }

  /* ------------------------------------------------------------------ feather cursor */
  function initCursor() {
    const c = $('[data-cursor]');
    if (!c || !fine || still || !hasGsap || !TT.motion?.cursor) return;
    html.classList.add('cursor-on');
    const label = $('.cursor__label', c);
    const qx = gsap.quickTo(c, 'x', { duration: 0.35, ease: 'power3' }), qy = gsap.quickTo(c, 'y', { duration: 0.35, ease: 'power3' });
    let shown = false;
    w.addEventListener('pointermove', e => {
      if (e.pointerType !== 'mouse') return;
      qx(e.clientX); qy(e.clientY);
      if (!shown) { c.classList.add('is-on'); shown = true; }
    }, { passive: true });
    d.addEventListener('pointerover', e => {
      const t = e.target.closest('[data-tickle]');
      c.classList.toggle('is-tickle', !!t);
      label.textContent = t ? (t.matches('.pc') ? 'tickle!' : 'hehe!') : '';
    });
    d.addEventListener('mouseleave', () => c.classList.remove('is-on'));
  }

  /* ------------------------------------------------------------------ tickle: long-press wiggle on touch */
  function initTickle(root = d) {
    $$('.pc[data-tickle]', root).forEach(card => {
      if (card.dataset.tickleBound) return; card.dataset.tickleBound = '1';
      let t;
      card.addEventListener('touchstart', () => { t = setTimeout(() => { card.classList.add('is-tickled', 'is-wiggle'); navigator.vibrate && navigator.vibrate(12); }, 380); }, { passive: true });
      const end = () => { clearTimeout(t); setTimeout(() => card.classList.remove('is-tickled', 'is-wiggle'), 1200); };
      card.addEventListener('touchend', end); card.addEventListener('touchmove', () => clearTimeout(t), { passive: true });
      card.addEventListener('contextmenu', e => { if (card.classList.contains('is-tickled')) e.preventDefault(); });
    });
  }

  /* ------------------------------------------------------------------ page transitions (sticker blob wipe) */
  function initTransitions() {
    const sw = $('[data-swipe]');
    if (!sw || still || !hasGsap || !TT.motion?.transitions) return;
    const blob = $('.swipe__blob', sw), logo = $('.swipe__logo', sw);
    const colors = ['var(--tt-lemon)', 'var(--tt-bubble)', 'var(--tt-teal)', 'var(--tt-lilac)', 'var(--tt-tang)'];
    // arriving: the blob is already covering (set by inline head script) → shrink it away
    if (html.classList.contains('is-arriving')) {
      let c = 'var(--tt-lemon)'; try { c = sessionStorage.getItem('tt-trans-c') || c; sessionStorage.removeItem('tt-trans'); } catch (e) {}
      gsap.set(blob, { background: c, scale: 1 }); gsap.set(logo, { opacity: 1 });
      gsap.timeline({ onComplete: () => html.classList.remove('is-arriving') })
        .add(logoGiggle(logo, 1), 0)
        .to(logo, { scale: 0, rotation: 20, duration: 0.25, ease: 'back.in(2)' }, 0.32)
        .to(blob, { scale: 0, duration: 0.55, ease: 'power3.inOut' }, 0.38);
    }
    d.addEventListener('click', e => {
      const a = e.target.closest('a[href]');
      if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      const url = new URL(a.href, location.href);
      if (url.origin !== location.origin || a.target === '_blank' || a.hasAttribute('download') || a.closest('[data-no-swipe]')) return;
      if (url.pathname === location.pathname && url.hash) return;
      if (/\/(checkout|account|cart\/|apps\/)/.test(url.pathname) || a.matches('[data-cart-open],[data-load-more],[data-filter-link]')) return;
      e.preventDefault();
      const c = colors[Math.floor(Math.random() * colors.length)];
      try { sessionStorage.setItem('tt-trans', '1'); sessionStorage.setItem('tt-trans-c', c); } catch (err) {}
      sw.style.setProperty('--x', e.clientX + 'px'); sw.style.setProperty('--y', e.clientY + 'px');
      html.classList.add('is-leaving');
      gsap.set(blob, { background: c, scale: 0 });
      gsap.timeline()
        .to(blob, { scale: 1, duration: 0.5, ease: 'power3.in' })
        .call(() => { location.href = url.href; }, null, 0.62)
        .fromTo(logo, { scale: 0, rotation: -20, opacity: 1 }, { scale: 1, rotation: 0, duration: 0.3, ease: 'back.out(3)' }, 0.25)
        .add(logoGiggle(logo, 1), 0.4);
      setTimeout(() => { location.href = url.href; }, 1200);
    });
    w.addEventListener('pageshow', e => { if (e.persisted) { html.classList.remove('is-leaving', 'is-arriving'); gsap.set(blob, { scale: 0 }); gsap.set(logo, { opacity: 0 }); } });
  }

  /* ------------------------------------------------------------------ menu */
  function initMenu() {
    const menu = $('[data-menu]'); if (!menu) return;
    const openBtn = $('[data-menu-open]'), bg = $('.menu__bg', menu);
    let tl = null;
    const open = () => {
      menu.hidden = false; html.classList.add('menu-open'); openBtn?.setAttribute('aria-expanded', 'true'); lockScroll(true);
      if (still || !hasGsap) { $('.menu__close', menu)?.focus(); return; }
      const r = openBtn.getBoundingClientRect();
      const at = `${r.left + r.width / 2}px ${r.top + r.height / 2}px`;
      menu.classList.add('is-animating');
      tl = gsap.timeline({ onComplete: () => menu.classList.remove('is-animating') })
        .fromTo(bg, { clipPath: `circle(0% at ${at})` }, { clipPath: `circle(150% at ${at})`, duration: 0.6, ease: 'power3.inOut' })
        .from($$('.menu__top, .menu__li', menu), { y: 60, rotation: () => gsap.utils.random(-12, 12), opacity: 0, duration: 0.8, ease: 'elastic.out(0.7,0.5)', stagger: 0.06 }, 0.25)
        .from($$('.menu__label, .menu__vibe, .menu .chip, .menu__foot > *', menu), { scale: 0, rotation: () => gsap.utils.random(-30, 30), duration: 0.6, ease: 'back.out(2.5)', stagger: 0.025 }, 0.4);
      $('.menu__close', menu)?.focus();
    };
    const close = () => {
      const done = () => { menu.hidden = true; html.classList.remove('menu-open'); openBtn?.setAttribute('aria-expanded', 'false'); lockScroll(false); openBtn?.focus(); };
      if (still || !hasGsap || !tl) return done();
      menu.classList.add('is-animating');
      tl.timeScale(2.2).reverse().eventCallback('onReverseComplete', () => { menu.classList.remove('is-animating'); done(); });
    };
    openBtn?.addEventListener('click', open);
    $$('[data-menu-close]', menu).forEach(b => b.addEventListener('click', close));
    menu.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });
  }

  /* ------------------------------------------------------------------ predictive search */
  function initSearch() {
    const box = $('[data-search]'); if (!box) return;
    const input = $('[data-search-input]', box), out = $('[data-search-results]', box);
    const open = () => {
      box.hidden = false; html.classList.add('search-open'); lockScroll(true); setTimeout(() => input.focus(), 30);
      if (hasGsap && !still) gsap.fromTo($('.srch__panel', box), { yPercent: -100 }, { yPercent: 0, duration: 0.55, ease: 'elastic.out(0.8,0.7)' });
    };
    const close = () => { box.hidden = true; html.classList.remove('search-open'); lockScroll(false); };
    $$('[data-search-open]').forEach(b => b.addEventListener('click', open));
    $$('[data-search-close]', box).forEach(b => b.addEventListener('click', close));
    box.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });
    let timer, ctrl;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (q.length < 2) { out.innerHTML = ''; return; }
      timer = setTimeout(async () => {
        ctrl && ctrl.abort(); ctrl = new AbortController();
        try {
          const r = await fetch(`${TT.routes.predictive}?q=${encodeURIComponent(q)}&resources[type]=product,collection&resources[limit]=6&section_id=tt-predictive`, { signal: ctrl.signal });
          const txt = await r.text();
          const doc = new DOMParser().parseFromString(txt, 'text/html');
          const sec = doc.querySelector('.shopify-section') || doc.body;
          out.innerHTML = sec.innerHTML;
          if (hasGsap && !still) pop($$('.ps__item, .ps__cols .chip', out), { y: 20, opacity: 0, rotation: -3, duration: 0.5, ease: 'back.out(2)', stagger: 0.04 });
        } catch (e) {}
      }, 220);
    });
  }

  /* ------------------------------------------------------------------ toast */
  let toastT;
  function toast(msg) {
    const t = $('[data-toast]'); if (!t) return;
    t.textContent = msg; t.classList.add('is-on'); clearTimeout(toastT);
    toastT = setTimeout(() => t.classList.remove('is-on'), 2200);
  }
  TT.toast = toast;

  /* ------------------------------------------------------------------ cart */
  const Cart = {
    el: null,
    open() {
      const dr = this.el; if (!dr) return;
      dr.hidden = false; lockScroll(true);
      if (hasGsap && !still) {
        gsap.fromTo($('.drawer__card', dr), { xPercent: 110, rotation: 6 }, { xPercent: 0, rotation: 0, duration: 0.7, ease: 'elastic.out(0.75,0.6)' });
        gsap.fromTo($('.drawer__scrim', dr), { opacity: 0 }, { opacity: 1, duration: 0.3 });
        gsap.from($$('.ci__row, .empty > *', dr), { x: 40, opacity: 0, duration: 0.5, ease: 'back.out(2)', stagger: 0.05, delay: 0.15 });
      }
      $('[data-cart-close]', dr)?.focus?.();
    },
    close() {
      const dr = this.el; if (!dr || dr.hidden) return;
      const done = () => { dr.hidden = true; lockScroll(false); };
      if (hasGsap && !still) {
        gsap.to($('.drawer__card', dr), { xPercent: 110, rotation: 6, duration: 0.4, ease: 'power3.in', onComplete: done });
        gsap.to($('.drawer__scrim', dr), { opacity: 0, duration: 0.3 });
      } else done();
    },
    render(sections) {
      const html = sections && sections['tt-cart-drawer'];
      if (!html) return this.refresh();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('.drawer__card');
      if (fresh) $('.drawer__card', this.el).innerHTML = fresh.innerHTML;
      const n = doc.querySelector('[data-cart-count]')?.textContent || '0';
      $$('[data-cart-count]').forEach(c => { c.textContent = (n === '0' && c.hasAttribute('data-hide-zero')) ? '' : n; });
      $$('[data-cart-open]').forEach(b => { b.classList.remove('is-bump'); void b.offsetWidth; b.classList.add('is-bump'); });
    },
    async refresh() {
      const r = await fetch(`${TT.routes.root}?sections=tt-cart-drawer`);
      this.render(await r.json());
    },
    async add(items, sourceImg) {
      const body = { items, sections: 'tt-cart-drawer', sections_url: location.pathname };
      const r = await fetch(`${TT.routes.cartAdd}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
      const data = await r.json();
      if (!r.ok || data.status) throw new Error(data.description || data.message || 'Could not add to bag');
      if (sourceImg) await flyToBag(sourceImg);
      this.render(data.sections);
      confetti();
      this.open();
      return data;
    },
    async change(key, quantity) {
      const r = await fetch(`${TT.routes.cartChange}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity, sections: 'tt-cart-drawer', sections_url: location.pathname }) });
      const data = await r.json();
      if (!r.ok || data.status) { toast(data.description || 'Hmm, that did not work'); return this.refresh(); }
      this.render(data.sections);
    },
    init() {
      this.el = $('[data-cart-drawer]');
      if (!this.el) return;
      d.addEventListener('click', e => {
        const o = e.target.closest('[data-cart-open]');
        if (o && !e.metaKey && !e.ctrlKey) { e.preventDefault(); this.open(); return; }
        if (e.target.closest('[data-cart-close]')) { this.close(); return; }
        const row = e.target.closest('.ci__row');
        if (row && this.el.contains(row)) {
          const input = $('[data-qty-input]', row);
          if (e.target.closest('[data-qty-plus]')) { row.classList.add('is-busy'); this.change(row.dataset.key, +input.value + 1); }
          else if (e.target.closest('[data-qty-minus]')) { row.classList.add('is-busy'); this.change(row.dataset.key, Math.max(0, +input.value - 1)); }
          else if (e.target.closest('[data-remove]')) {
            if (hasGsap && !still) gsap.to(row, { x: 120, rotation: 8, opacity: 0, duration: 0.35, ease: 'power2.in', onComplete: () => this.change(row.dataset.key, 0) });
            else this.change(row.dataset.key, 0);
          }
        }
        const q = e.target.closest('[data-quick-add]');
        if (q && q.tagName === 'BUTTON') {
          e.preventDefault();
          const card = q.closest('.pc'); const img = card && (card.querySelector('.pc__cut') || card.querySelector('.pc__photo'));
          q.classList.add('is-pressed');
          this.add([{ id: +q.dataset.quickAdd, quantity: 1 }], img).catch(err => toast(err.message)).finally(() => q.classList.remove('is-pressed'));
        }
      });
      this.el.addEventListener('change', e => {
        if (e.target.matches('[data-qty-input]')) { const row = e.target.closest('.ci__row'); this.change(row.dataset.key, Math.max(0, +e.target.value)); }
        if (e.target.matches('[data-cart-note]')) fetch(`${TT.routes.cartUpdate}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: e.target.value }) }).then(() => toast('Gift note saved ✓'));
      });
      this.el.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });
    }
  };
  TT.cart = Cart;

  /* product image flies in an arc into the bag pill */
  function flyToBag(img) {
    return new Promise(res => {
      if (!hasGsap || still || !img) return res();
      const target = [...$$('[data-cart-open]')].find(b => b.offsetParent !== null);
      if (!target) return res();
      const a = img.getBoundingClientRect(), b = target.getBoundingClientRect();
      const clone = img.cloneNode(); clone.removeAttribute('srcset'); clone.removeAttribute('loading');
      Object.assign(clone.style, { position: 'fixed', left: a.left + 'px', top: a.top + 'px', width: a.width + 'px', height: a.height + 'px', zIndex: 99, objectFit: 'contain', pointerEvents: 'none', margin: 0 });
      d.body.appendChild(clone);
      const dx = b.left + b.width / 2 - (a.left + a.width / 2), dy = b.top + b.height / 2 - (a.top + a.height / 2);
      gsap.timeline({ onComplete: () => { clone.remove(); res(); } })
        .to(clone, { x: dx, duration: 0.7, ease: 'power1.inOut' }, 0)
        .to(clone, { y: dy, duration: 0.7, ease: 'back.in(1.6)' }, 0)
        .to(clone, { scale: 0.12, rotation: 360, duration: 0.7, ease: 'power2.in' }, 0);
    });
  }
  /* a little burst of sticker confetti from the bag */
  function confetti() {
    if (!hasGsap || still) return;
    const target = [...$$('[data-cart-open]')].find(b => b.offsetParent !== null); if (!target) return;
    const r = target.getBoundingClientRect();
    const ids = ['s-star', 's-heart', 's-spark', 's-flower'];
    for (let i = 0; i < 12; i++) {
      const s = d.createElementNS('http://www.w3.org/2000/svg', 'svg');
      s.setAttribute('viewBox', '0 0 100 100'); s.innerHTML = `<use href="#${ids[i % 4]}"/>`;
      Object.assign(s.style, { position: 'fixed', left: r.left + r.width / 2 - 10 + 'px', top: r.top + r.height / 2 - 10 + 'px', width: '20px', height: '20px', zIndex: 98, pointerEvents: 'none' });
      d.body.appendChild(s);
      gsap.to(s, { x: gsap.utils.random(-120, 120), y: gsap.utils.random(-30, 160), rotation: gsap.utils.random(-360, 360), scale: gsap.utils.random(0.8, 1.6), opacity: 0, duration: gsap.utils.random(0.8, 1.3), ease: 'power2.out', onComplete: () => s.remove() });
    }
  }
  TT.flyToBag = flyToBag;

  /* ------------------------------------------------------------------ wishlist (saved on this device) */
  function initWish(root = d) {
    const list = store.get('tt-wish', []);
    $$('[data-wish]', root).forEach(b => {
      b.setAttribute('aria-pressed', list.includes(b.dataset.wish) ? 'true' : 'false');
      if (b.dataset.wishBound) return; b.dataset.wishBound = '1';
      b.addEventListener('click', e => {
        e.preventDefault();
        const l = store.get('tt-wish', []), h = b.dataset.wish, i = l.indexOf(h);
        i > -1 ? l.splice(i, 1) : l.unshift(h);
        store.set('tt-wish', l.slice(0, 60));
        $$(`[data-wish="${CSS.escape(h)}"]`).forEach(x => x.setAttribute('aria-pressed', i > -1 ? 'false' : 'true'));
        if (hasGsap && !still) pop(b, { scale: 0.6, rotation: -20, duration: 0.6, ease: 'elastic.out(1,0.4)' });
        toast(i > -1 ? 'Removed from saved' : 'Saved! ♥');
        wishCount();
      });
    });
    wishCount();
  }
  function wishCount() {
    const n = store.get('tt-wish', []).length;
    $$('[data-wish-count]').forEach(c => { c.textContent = n; c.hidden = !n; });
  }
  async function initWishPage() {
    const grid = $('[data-wishlist-grid]'); if (!grid) return;
    const list = store.get('tt-wish', []);
    if (!list.length) { $('[data-wishlist-empty]').hidden = false; return; }
    const cards = await Promise.all(list.map(h => fetch(`${TT.routes.root}products/${h}?section_id=tt-card`).then(r => r.ok ? r.text() : '').catch(() => '')));
    grid.innerHTML = cards.map(c => { const doc = new DOMParser().parseFromString(c, 'text/html'); return doc.querySelector('.pc')?.outerHTML || ''; }).join('');
    if (!grid.children.length) $('[data-wishlist-empty]').hidden = false;
    TT.reveal(grid);
  }

  /* ------------------------------------------------------------------ WhatsApp bubble says hi once */
  function initWa() {
    const f = $('.wa-float'); if (!f) return;
    try { if (sessionStorage.getItem('tt-wa-hi')) return; } catch (e) {}
    setTimeout(() => { f.classList.add('is-hi'); try { sessionStorage.setItem('tt-wa-hi', '1'); } catch (e) {} setTimeout(() => f.classList.remove('is-hi'), 4000); }, 7000);
  }

  /* ------------------------------------------------------------------ product recommendations (Twirl with it) */
  function initRelated() {
    $$('[data-related]').forEach(async sec => {
      if (sec.children.length) return;
      try {
        const r = await fetch(sec.dataset.url); const t = await r.text();
        const doc = new DOMParser().parseFromString(t, 'text/html');
        const inner = doc.querySelector('[data-related]');
        if (inner && inner.innerHTML.trim()) { sec.innerHTML = inner.innerHTML; TT.reveal(sec); initTitlesIn(sec); }
      } catch (e) {}
    });
  }
  function initTitlesIn(root) {
    if (still || !hasGsap) return;
    $$('[data-tumble]', root).forEach(el => ScrollTrigger.create({ trigger: el, start: 'top 90%', once: true, onEnter: () => tumble(el) }));
  }

  /* ------------------------------------------------------------------ footer accordions open on desktop */
  function initFooterCols() {
    const set = () => $$('[data-ft-col]').forEach(c => { if (innerWidth >= 900) c.open = true; });
    set(); w.addEventListener('resize', set);
  }

  /* ------------------------------------------------------------------ go */
  async function start() {
    initLenis();
    initChrome();
    initMarquees();
    initMenu();
    initSearch();
    Cart.init();
    initWish();
    initWishPage();
    initAgeMemory();
    initTickle();
    initCursor();
    initTransitions();
    initWa();
    initFooterCols();
    if (!hasGsap) { release(); $$('[data-reveal]').forEach(i => i.classList.add('is-in')); return; }
    initHero();
    initTitles();
    initReveal();
    initSpin();
    initVibes();
    initTwirlBand();
    initTape();
    initRunway();
    initFooter();
    initRelated();
    release();
    await runLoader();
    TT.heroIntro && TT.heroIntro.play();
    // fonts can shift layout: refresh triggers once they are in
    d.fonts && d.fonts.ready.then(() => ScrollTrigger.refresh());
    w.addEventListener('load', () => ScrollTrigger.refresh());
    d.dispatchEvent(new CustomEvent('tt:ready'));
  }
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', start); else start();
})();
